import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

import { FormHelperText, makeStyles, Select as MuiSelect, TextField } from '@material-ui/core';

import makePath from '../../utils/makePath';
import generateId from '../../utils/generateId';
import { deleteItem, setItem } from '../../actions';
import { useDirty, useFormElementCleanUp, useValidation } from '../../hooks';
import { FormElementWrapper, ErrorHelpText } from '.';
import { ampmOptions } from '../../config/timeline';

const useStyles = makeStyles(theme => ({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    width: 204,
  },
  innerWrapper: {
    display: 'flex',
  },
  time: {
    flex: '1 1 95px'
  },
  ampm: {
    flex: '1 1 70px',
    marginLeft: 10,
  },
  helper: {
    width: 175,
  }
}))

/**
 * 
 * @todo
 * If this was to be truly reusable,
 * we would want to refactor to return something like 
 * [name]: {
 *   time: 'HH:MM',
 *   ampm: 'AM'
 * }
 * 
 * Could also stand to accept more customization, like 24hr time, and leading 0s.
 * 
 */

export const TimeInput = React.memo(({given = {},label,name,...props}) => {
  
  const [id] = useState(generateId('time-'));

  const classes = useStyles();
  const dispatch = useDispatch();
  const setDirty = useDirty();

  const path = makePath(props.path,name);
  useFormElementCleanUp(path);


  const [value, setValue] = useState('');
  const [invalid, setInvalid] = useState(false);
  const [ampm, setAmpm] = useState('AM'); 

  // Validation conditions
  const validate = () => {

    if (!value && !props.optional) {
      setError('Missing! This field is required.');
    } else {
      setError('')
    }
    
  }
  
  // Apply validation conditions
  const [error, setError] = useValidation(validate,value,path,id);

  // Set user input for time in state
  const handleTimeChange = (e) => {
    setValue(e.target.value)
    setDirty();
  }

  // Set user input for am/pm in state
  const handleAmpmChange = (e) => {
    setAmpm(e.target.value)
  }


  // Update store when state changes
  useEffect(() => {
    // Add time to store
    if ( value && value.match(/^([1-9]|[1][0-2]):([0-5][0-9])$/) ) {
      dispatch(setItem(path, {
        numbers: value,
        ampm: ampm
      }));
    } 
    // If numbers is empty, delete whole time (numbers and am/pm) from store
    else if (value === '') {
      dispatch(deleteItem(path));
    }
  }, [ampm,dispatch,path,value])


  // Set state from given value, if provided
  useEffect(() => {
    if (given.numbers) {
      setValue(given.numbers);
    }
    if (given.ampm) {
      setAmpm(given.ampm);
    }
  }, [given]);

  // Validate that we have a real time on blur
  // Makes user immediately aware something is wrong since 
  // we don't sent an invalid value in state. This is due to 
  // parsing issues in the template
  const onBlur = () => {
    if ( value === '' || value.match(/^([1-9]|[1][0-2]):([0-5][0-9])$/) ) {
      setInvalid(false)
    } else {
      setInvalid(true)
    }
  }

  return (
    <FormElementWrapper className={classes.wrapper}>
      <div className={classes.innerWrapper}>

        {/* "Numbers" input, i.e. 10:00 */}
        <TextField
          className={classes.time}
          label={label}
          onChange={handleTimeChange}
          onBlur={onBlur}
          value={value}
          error={invalid || !!error}
          name={name}
          placeholder="12:00"
          InputProps={{
            'aria-describedby': `${id}-helper-text ${id}-error`
          }}
          variant="outlined"
        />
        
        {/* AM/PM selector */}
        <MuiSelect
          className={classes.ampm}
          native
          variant="outlined"
          error={!!error}
          value={ampm}
          onChange={handleAmpmChange}
          name={name + '-ampm'}
          inputProps={{
            name: name + '-ampm', 
            id: id + '-ampm',
            'aria-label': 'AM/PM'
          }}
        >
          {
            ampmOptions.map((option, index) => {
              return <option key={index} value={option.value}>{option.label}</option>
            })
          }
        </MuiSelect>
    </div>

      {invalid ? 
        <ErrorHelpText id={id} error="Error! You need to provide time in the format 12:00, with no leading ‘0’, e.g., 1:00." />
      : error ? 
        <ErrorHelpText id={id} error={error} />
      : '' } 
    
      <FormHelperText id={`${id}-helper-text`}>
        {props.help}
      </FormHelperText>

    </FormElementWrapper>
  )

})


TimeInput.propTypes = {
  classes: PropTypes.string,
  help: PropTypes.string,
  label: PropTypes.string,
  name: PropTypes.string.isRequired,
  path: PropTypes.string
}